import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { erabCellChartConfig } from '../cellChartConfigs';

/**
 * Props for the ErabEstablishmentCell component
 */
interface ErabEstablishmentCellProps {
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
  /** Current fullscreen state */
  isFullscreen: boolean;
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
}

/**
 * ERAB Establishment Success Rate Chart (LTE Cell Level)
 * 
 * Displays E-UTRAN Radio Access Bearer (ERAB) establishment metrics for individual LTE cells:
 * - ERAB Establishment Success (count)
 * - ERAB Establishment Attempt (count)
 * - ERAB Establishment Success Rate (percentage)
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level ERAB metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <ErabEstablishmentCell
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 *   cellName="173716_145"
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (erabCellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/erab-establishment?cell_name={cellName}
 */
const ErabEstablishmentCell: React.FC<ErabEstablishmentCellProps> = ({ 
  onFullscreenToggle, 
  isFullscreen,
  cellName
}) => {
  // Memoize additionalParams to prevent infinite re-renders
  const additionalParams = useMemo(() => ({ cell_name: cellName }), [cellName]);

  return (
    <SiteCharts 
      config={erabCellChartConfig}
      onFullscreenToggle={onFullscreenToggle}
      isFullscreen={isFullscreen}
      additionalParams={additionalParams}
    />
  );
};

export default ErabEstablishmentCell;
